import React, { useState } from 'react';
import { EmployeeTaskView } from './EmployeeTaskView';
import { useStore } from '../../store/useStore';
import { Card } from '../ui/Card';
import { MessageSquare, ListTodo, Bell, Calendar, Sparkles, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Button } from '../ui/Button';
import { TaskForm } from '../tasks/TaskForm';
import { clsx } from 'clsx';

export const EmployeeDashboard: React.FC = () => {
  const { currentUser, messages } = useStore();
  const [showTaskForm, setShowTaskForm] = useState(false);
  
  const departmentMessages = messages.filter(msg => 
    msg.departments.includes(currentUser?.department!)
  );

  const recentMessages = departmentMessages.slice(0, 3);
  const unreadCount = departmentMessages.filter(msg => !msg.acknowledgments.includes(currentUser?.id || '')).length;

  const quickActions = [
    { icon: ListTodo, label: 'Create Task', onClick: () => setShowTaskForm(true), color: 'bg-emerald-100 text-emerald-600' },
    { icon: Bell, label: 'Notifications', count: unreadCount, color: 'bg-blue-100 text-blue-600' },
    { icon: Calendar, label: 'Schedule', color: 'bg-purple-100 text-purple-600' },
    { icon: Users, label: 'Team', color: 'bg-amber-100 text-amber-600' },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header Section */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm p-4 sm:p-8 border border-gray-100"
      >
        <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              src={currentUser?.avatar}
              alt={currentUser?.name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full ring-4 ring-emerald-500 ring-offset-4 shadow-lg"
            />
            <div>
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1"
              >
                Welcome back, {currentUser?.name}
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-gray-600 flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-emerald-500" />
                {format(new Date(), 'EEEE, MMMM d')}
              </motion.p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {quickActions.map((action, index) => (
            <motion.button
              key={action.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={action.onClick}
              className="relative p-4 sm:p-6 rounded-xl bg-white border-2 border-gray-100 hover:border-emerald-500 active:bg-gray-50 transition-all group touch-manipulation"
            >
              <div className={clsx("p-2 sm:p-3 rounded-xl mb-2 sm:mb-3 transition-colors", action.color)}>
                <action.icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-gray-900">{action.label}</h3>
              {action.count && (
                <span className="absolute top-2 right-2 sm:top-4 sm:right-4 min-w-[1.25rem] h-5 flex items-center justify-center px-1.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                  {action.count}
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tasks Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <EmployeeTaskView />
        </motion.div>
        
        {/* Communications Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Card className="p-6 h-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Department Communications</h2>
                <p className="text-sm text-gray-600">Recent messages and updates</p>
              </div>
            </div>

            <div className="space-y-4">
              <AnimatePresence>
                {recentMessages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={clsx(
                      "p-3 sm:p-4 rounded-lg transition-all",
                      message.acknowledgments.includes(currentUser?.id || '')
                        ? "bg-gray-50"
                        : "bg-blue-50 shadow-md"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                      <div className="min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-medium text-gray-900">{message.title}</h3>
                          {!message.acknowledgments.includes(currentUser?.id || '') && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full sm:hidden">
                              New
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{message.content}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-gray-500">
                          <img
                            src={message.sender.avatar}
                            alt={message.sender.name}
                            className="w-5 h-5 rounded-full"
                          />
                          <span>{message.sender.name}</span>
                          <span className="hidden sm:inline">•</span>
                          <span>{format(new Date(message.createdAt), 'MMM d, h:mm a')}</span>
                        </div>
                      </div>
                      {!message.acknowledgments.includes(currentUser?.id || '') && (
                        <span className="hidden sm:inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {recentMessages.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No messages yet</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {showTaskForm && (
        <TaskForm
          department={currentUser?.department}
          onSuccess={() => setShowTaskForm(false)}
        />
      )}
    </div>
  );
};